import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { BarSeriesType } from '@mui/x-charts/models';
import { FC } from 'react';

const MAX_VISIBLE_ITEMS = 3;

type ProductUsageBreakdownChartLegendProps = {
  series: BarSeriesType[];
};

export const ProductUsageBreakdownChartLegend: FC<ProductUsageBreakdownChartLegendProps> = ({ series }) => {
  const visibleItems = series.slice(0, MAX_VISIBLE_ITEMS);
  const hiddenItems = series.slice(MAX_VISIBLE_ITEMS);
  const hasOverflow = hiddenItems.length > 0;

  return (
    <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap', marginTop: 4, rowGap: 1 }}>
      {visibleItems.map((item, index) => (
        <LegendItem
          key={String(item.id ?? item.dataKey ?? index)}
          color={item.color}
          label={resolveLabel(item.label)}
        />
      ))}
      {hasOverflow && (
        <Tooltip
          arrow
          placement="top"
          slotProps={{
            tooltip: {
              sx: {
                backgroundColor: '#ffffff',
                color: '#323232',
                border: '1px solid #E8EBF2',
                boxShadow: '0px 4px 16px rgba(15, 23, 42, 0.08)',
                maxWidth: 320,
                padding: 1.5,
              },
            },
            arrow: {
              sx: {
                color: '#ffffff',
                '&::before': { border: '1px solid #E8EBF2' },
              },
            },
          }}
          title={
            <Stack spacing={1}>
              {hiddenItems.map((item, index) => (
                <LegendItem
                  key={String(item.id ?? item.dataKey ?? index)}
                  color={item.color}
                  label={resolveLabel(item.label)}
                />
              ))}
            </Stack>
          }
        >
          <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
            <Box
              sx={{
                width: 12,
                height: 8,
                borderRadius: '2px',
                backgroundColor: '#fff',
                border: '1px solid #C0C7D4',
                flexShrink: 0,
              }}
            />
            <Typography
              variant="body2"
              data-vy="product-usage-chart-legend-overflow"
              sx={{
                cursor: 'pointer',
                color: 'text.secondary',
                fontWeight: 500,
              }}
            >
              +{hiddenItems.length} more
            </Typography>
          </Stack>
        </Tooltip>
      )}
    </Stack>
  );
};

type LegendItemProps = {
  color?: string;
  label?: string;
};

const LegendItem: FC<LegendItemProps> = ({ color, label }) => (
  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
    <Box
      sx={{
        width: 12,
        height: 8,
        borderRadius: '2px',
        backgroundColor: color,
        flexShrink: 0,
      }}
    />
    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
      {label ?? '—'}
    </Typography>
  </Stack>
);

function resolveLabel(label: BarSeriesType['label']): string | undefined {
  return typeof label === 'function' ? label('legend') : label;
}
